import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Info, TrendingUp, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";

import { getSuburbInsights } from "../api/prospectingReportsApi";

const TEAL = "#55c5d0";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-AU")}`);
const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const plain = (n) => (n == null ? "—" : String(n));

// Every tile the report can carry. `format` is how it prints, `pick` reads it
// out of the merged headline block the API returns.
const TILES = [
  { key: "median_sale_price", label: "Median sale price", format: money },
  { key: "sold_volume", label: "Sold volume", format: plain },
  { key: "price_growth_pct", label: "Price growth", format: pct },
  { key: "median_days_on_market", label: "Median days on market", format: plain },
  { key: "rental_yield_pct", label: "Rental yield", format: pct },
  { key: "clearance_rate_pct", label: "Clearance rate", format: pct },
];

const auDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-AU");
};

/**
 * Step 2 — Select insights.
 *
 * The agent ticks which suburb statistics go on the report. Unlike Ignite,
 * which is fed by PropTrack, our figures come from Collings' own sold data plus
 * whatever Homer has cached, so each tile shows where its number came from and
 * a tile we cannot compute is disabled rather than shown as zero. A vendor
 * reading "0% growth" would draw the wrong conclusion from a gap in our data.
 */
const CmaInsightsStep = ({ report, insights, onChange }) => {
  const [data, setData] = useState(insights?.headline ? insights : null);
  const [loading, setLoading] = useState(!insights?.headline);
  const [enabled, setEnabled] = useState(
    () => new Set(insights?.enabled || TILES.map((t) => t.key)),
  );

  const load = useCallback(async () => {
    if (!report?.suburb) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: fetched } = await getSuburbInsights({
        suburb: report.suburb,
        state: report.state || "",
        postcode: report.postcode || "",
        property_type: report.property_type || "",
        bedrooms: report.bedrooms || "",
      });
      setData(fetched);
    } catch (error) {
      toast.error("Could not load suburb statistics.");
    } finally {
      setLoading(false);
    }
  }, [report]);

  useEffect(() => { load(); }, [load]);

  // Push the snapshot up whenever the agent's selection or the data changes, so
  // the wizard saves exactly what will be rendered.
  useEffect(() => {
    if (!data) return;
    onChange({ ...data, enabled: Array.from(enabled) });
    // onChange is stable in the wizard; including it here would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enabled]);

  const toggle = (key) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const headline = data?.headline || {};
  const hasNothing = !data?.has_data;

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-base font-bold text-gray-900">
          {report?.suburb ? `${report.suburb} insights` : "Suburb insights"}
        </h3>
        <p className="text-sm text-gray-500">
          Tick the statistics to include in the report. Anything we cannot calculate is left out
          rather than shown as zero.
        </p>
      </div>

      {hasNothing && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            We have no market statistics for {report?.suburb || "this suburb"} yet. Collings has no
            recorded sales here in the reporting window, and the Homer feed has no cached row.
            The report will still show your selected comparables and appraisal.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {TILES.map((tile) => {
          const value = headline[tile.key];
          const available = value != null;
          const isOn = available && enabled.has(tile.key);
          return (
            <button
              key={tile.key}
              type="button"
              disabled={!available}
              onClick={() => toggle(tile.key)}
              className={`text-left p-3 rounded-xl border transition-colors ${
                !available
                  ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                  : isOn
                    ? "border-[#55c5d0] bg-[#f0fbfc]"
                    : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                  {tile.label}
                </p>
                <span
                  className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                    isOn ? "border-transparent" : "border-gray-300"
                  }`}
                  style={isOn ? { backgroundColor: TEAL } : undefined}
                >
                  {isOn && <span className="text-white text-[10px] leading-none">✓</span>}
                </span>
              </div>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {available ? tile.format(value) : "No data"}
              </p>
            </button>
          );
        })}
      </div>

      {/* Provenance. A vendor-facing number needs a defensible origin. */}
      <div className="mt-4 flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-3">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-400" />
        <div>
          <p>
            Price and volume come from <strong>Collings' own recorded sales</strong>
            {data?.collings?.sold_volume
              ? ` (${data.collings.sold_volume} sale${data.collings.sold_volume === 1 ? "" : "s"} in the last ${data.collings.window_months} months)`
              : ""}.
            {data?.homer
              ? ` Yield and clearance rate come from the Homer feed, last synced ${auDate(data.homer.as_of) || "recently"}.`
              : " Homer has no cached data for this suburb, so yield and clearance rate are unavailable."}
          </p>
        </div>
      </div>

      {/* The sales behind the numbers. */}
      {data?.recent_sales?.length > 0 && (
        <div className="mt-5">
          <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4" style={{ color: TEAL }} />
            Recent Collings sales in {report?.suburb}
          </h4>
          <div className="space-y-1">
            {data.recent_sales.map((sale, index) => (
              <div key={`${sale.address}-${index}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100">
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">{sale.address}</p>
                  <p className="text-xs text-gray-500">
                    {sale.bedrooms != null && `${sale.bedrooms} bed · `}
                    {auDate(sale.sold_date)}
                  </p>
                </div>
                <p className="text-sm font-bold text-gray-900 whitespace-nowrap">
                  {sale.suppressed ? "Price withheld" : money(sale.sold_price)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CmaInsightsStep;
